import { defineComponent, computed, openBlock, createElementBlock, normalizeClass, unref, createVNode, Transition, withCtx, renderSlot, h, useCssVars, useTemplateRef, ref, watch, nextTick, createBlock, normalizeStyle, toDisplayString, createElementVNode, createCommentVNode, createTextVNode } from 'vue';
import { _ as _export_sfc, u as useTheme, a as useI18n, b as useWindowSize, c as useElementSize, d as useFocus, o as onKeyStroke, F as FloatingWindow, A as ActionButton, e as _export_sfc$1 } from '../review.es.js';
import { u as useTextDirection, a as useAutoClose, M as MainRating, P as PropertyRanking, R as RecordGrid, b as ReviewList, c as ProvidersInfo, d as useTailwindTheme } from './useTailwindTheme.DuMV2zVC.js';
import { u as useDevicePosition, V as ViewType, F as FloatingTrigger, A as ArrowBtn } from './floating.types.D6Lk9tVC.js';

const _sfc_main$1 = /* @__PURE__ */ defineComponent({
  __name: "FloatingBox",
  props: {
    position: {}
  },
  setup(__props) {
    const props = __props;
    const devicePosition = useDevicePosition(props.position);
    const appearTransition = computed(() => {
      switch (devicePosition.value) {
        case "UL":
        case "UR":
          return "slide-down";
        case "CL":
          return "slide-right";
        case "CR":
          return "slide-left";
        default:
          return "slide-up";
      }
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass([
          "widget",
          `widget--${unref(devicePosition)}`
        ]),
        "data-testid": "widget"
      }, [
        createVNode(Transition, {
          name: appearTransition.value,
          appear: ""
        }, {
          default: withCtx(() => [
            renderSlot(_ctx.$slots, "default", {}, void 0, true)
          ]),
          _: 3
        }, 8, ["name"])
      ], 2);
    };
  }
});

const FloatingBox = /* @__PURE__ */ _export_sfc(_sfc_main$1, [["__scopeId", "data-v-6ec2e81b"]]);

const icon = (props, context) => {
    const xmlns = "http://www.w3.org/2000/svg";
    const svgCode = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
  <path fill-rule="evenodd" d="M4.47 6.97a.75.75 0 011.06 0L10 11.44l4.47-4.47a.75.75 0 111.06 1.06l-5 5a.75.75 0 01-1.06 0l-5-5a.75.75 0 010-1.06z" clip-rule="evenodd"/>
</svg>
`;
    
    const svgRoot = new DOMParser().parseFromString(svgCode, 'image/svg+xml').firstChild;
    
    if (props.title) {
      const title = document.createElementNS(xmlns, 'title');
      title.textContent = props.title;
      svgRoot.appendChild(title);
    }

    const svgAttributes = Array
      .from(svgRoot?.attributes ?? [])
      .reduce((acc, curr) => ({
        ...acc,
        [curr.name]: curr.value
      }));

    return h(
      'svg',
      {
        ...svgAttributes,
        ...context.attrs,
        role: 'img',
        innerHTML: svgRoot.innerHTML
      },
    );
  };

const _hoisted_1 = { key: 0 };
const _hoisted_2 = ["aria-label"];
const _hoisted_3 = {
  key: 0,
  class: "py-3 px-4"
};
const _hoisted_4 = { class: "text-base" };
const _hoisted_5 = { class: "text-grey-500" };
const _hoisted_6 = { class: "flex items-center p-3 gap-4 order-first reviews-header" };
const expandedHeaderHeight = 38;
const widgetMargin = 40;
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "FloatingDesktopReview",
  props: {
    config: {}
  },
  setup(__props, { expose: __expose }) {
    useCssVars((_ctx) => ({
      "f74ae526": props.config.zIndex,
      "4194eeb6": unref(theme).fontColor,
      "33ceef96": widgetSize.value.width,
      "2c400957": widgetSize.value.height,
      "6a91d9cb": `${widgetMargin}px`,
      "12d450b0": unref(theme).actionColor.base,
      "3449c595": unref(theme).backgroundColor.washed
    }));
    const props = __props;
    const rootEl = useTemplateRef("rootEl");
    const titleEl = useTemplateRef("titleEl");
    const reviewsEl = useTemplateRef("reviewsEl");
    const currentView = ref(
      props.config.showClosed ? ViewType.COLLAPSED : ViewType.EXPANDED
    );
    const theme = useTheme();
    const { t } = useI18n();
    const expandedBodyElement = ref();
    const { height: windowHeight } = useWindowSize();
    const {
      height: expandedBodyElementHeight
    } = useElementSize(expandedBodyElement);
    const tailwindTheme = useTailwindTheme();
    const textDirection = useTextDirection(props.config.isRightAlign);
    const { clear: clearAutoClose } = useAutoClose({
      enabled: !props.config.showClosed,
      delay: props.config.closeTime,
      callback() {
        currentView.value = ViewType.COLLAPSED;
      }
    });
    const { focused: titleFocused } = useFocus(titleEl);
    const widgetSizes = computed(() => ({
      [ViewType.COLLAPSED]: {
        width: "240px",
        height: "52px"
      },
      [ViewType.EXPANDED]: {
        width: "220px",
        height: `${expandedHeaderHeight + expandedBodyElementHeight.value}px`
      },
      [ViewType.REVIEWS]: {
        width: "520px",
        height: `${Math.min(620, windowHeight.value - widgetMargin)}px`
      }
    }));
    const showReviewersList = ref(!props.config.showSummarized);
    const widgetSize = computed(() => widgetSizes.value[currentView.value]);
    const triggerPosition = computed(() => {
      if (props.config.devicePosition === "UL" || props.config.devicePosition === "UR") {
        return "bottom";
      }
      return "top";
    });
    function toggleView() {
      clearAutoClose();
      currentView.value = currentView.value === ViewType.EXPANDED ? ViewType.COLLAPSED : ViewType.EXPANDED;
    }
    function collapse() {
      clearAutoClose();
      currentView.value = ViewType.COLLAPSED;
    }
    watch(
      currentView,
      async (newValue) => {
        await nextTick();
        if (newValue === ViewType.REVIEWS) {
          reviewsEl.value?.querySelector("button")?.focus();
        }
      }
    );
    onKeyStroke("Escape", collapse, { target: rootEl });
    onKeyStroke(["Enter", "Space", "Tab"], () => {
      clearAutoClose();
    }, { target: rootEl });
    __expose({
      collapse
    });
    return (_ctx, _cache) => {
      return openBlock(), createBlock(unref(FloatingBox), {
        ref_key: "rootEl",
        ref: rootEl,
        role: "region",
        "aria-label": unref(t)("Review Summary"),
        position: props.config.devicePosition,
        class: "floating-review",
        onMouseenter: unref(clearAutoClose)
      }, {
        default: withCtx(() => [
          createVNode(unref(FloatingWindow), { class: "floating-review__window" }, {
            default: withCtx(() => [
              currentView.value === unref(ViewType).EXPANDED || currentView.value === unref(ViewType).COLLAPSED ? (openBlock(), createElementBlock("div", _hoisted_1, [
                createVNode(unref(FloatingTrigger), {
                  open: currentView.value !== unref(ViewType).COLLAPSED,
                  position: triggerPosition.value,
                  onClick: toggleView,
                  style: normalizeStyle({
                    height: currentView.value === unref(ViewType).COLLAPSED ? widgetSizes.value[unref(ViewType).COLLAPSED].height : `${expandedHeaderHeight}px`
                  }),
                  "data-testid": "floatingTrigger"
                }, {
                  default: withCtx(() => [
                    createVNode(Transition, {
                      name: "fade",
                      mode: "out-in",
                      onAfterEnter: _cache[0] || (_cache[0] = () => {
                        if (currentView.value === unref(ViewType).EXPANDED) {
                          titleFocused.value = true;
                        }
                      })
                    }, {
                      default: withCtx(() => [
                        currentView.value === unref(ViewType).COLLAPSED ? (openBlock(), createBlock(MainRating, {
                          key: 0,
                          data: props.config.payload.reviewAverages,
                          colors: {
                            darkText: unref(theme).mainColor.contrasted,
                            lightText: unref(theme).mainColor.contrasted
                          },
                          "text-style": {
                            score: {
                              size: "16px",
                              weight: "600"
                            },
                            average: {
                              size: "24px",
                              weight: "600"
                            },
                            note: {
                              size: "14px",
                              weight: "600"
                            },
                            based: {
                              size: "12px",
                              weight: "400"
                            }
                          },
                          tooltip: {
                            ...props.config.calculate.tooltip,
                            placement: "top"
                          },
                          "compact-size": "big",
                          "data-testid": "mainRatingCollapsed"
                        }, null, 8, ["data", "colors", "tooltip"])) : (openBlock(), createElementBlock("h1", {
                          key: 1,
                          ref_key: "titleEl",
                          ref: titleEl,
                          tabindex: "-1",
                          class: "font-semibold text-s leading-4 text-left"
                        }, toDisplayString(unref(t)("Review Summary")), 513))
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["open", "position", "style"]),
                currentView.value === unref(ViewType).EXPANDED ? (openBlock(), createElementBlock("div", {
                  key: 0,
                  ref_key: "expandedBodyElement",
                  ref: expandedBodyElement,
                  "data-testid": "expandedBody"
                }, [
                  createElementVNode("div", {
                    class: normalizeClass([
                      "pt-3 px-4",
                      { "pb-4": showReviewersList.value },
                      { "cursor-pointer": !showReviewersList.value },
                      { "border-b border-b-grey-100": currentView.value === unref(ViewType).EXPANDED }
                    ]),
                    onClick: _cache[2] || (_cache[2] = () => {
                      if (!showReviewersList.value) {
                        showReviewersList.value = true;
                      }
                    })
                  }, [
                    createVNode(MainRating, {
                      data: props.config.payload.reviewAverages,
                      colors: {
                        darkText: unref(tailwindTheme).colors.grey[900],
                        lightText: unref(tailwindTheme).colors.grey[500]
                      },
                      tooltip: {
                        ...props.config.calculate.tooltip,
                        placement: "top"
                      },
                      orientation: "vertical"
                    }, null, 8, ["data", "colors", "tooltip"]),
                    props.config.calculate.rankingConfig.visible ? (openBlock(), createBlock(PropertyRanking, {
                      key: 0,
                      config: props.config.calculate.rankingConfig,
                      inline: "",
                      class: "mt-2"
                    }, null, 8, ["config"])) : createCommentVNode("", true),
                    !showReviewersList.value ? (openBlock(), createElementBlock("button", {
                      key: 1,
                      type: "button",
                      onClick: _cache[1] || (_cache[1] = ($event) => showReviewersList.value = true),
                      "aria-label": unref(t)("Show Reviewers"),
                      class: "w-full mt-2 border-0 outline-none text-grey-500 hover:text-grey-900 transition-colors duration-300"
                    }, [
                      createVNode(unref(icon), {
                        "aria-label": unref(t)("Arrow icon"),
                        class: "w-4 h-4 mx-auto"
                      }, null, 8, ["aria-label"])
                    ], 8, _hoisted_2)) : createCommentVNode("", true)
                  ], 2),
                  showReviewersList.value ? (openBlock(), createElementBlock("div", _hoisted_3, [
                    createVNode(unref(RecordGrid), {
                      items: props.config.calculate.providers,
                      direction: "column",
                      class: "mb-4 leading-6"
                    }, {
                      value: withCtx(({ item }) => [
                        createElementVNode("strong", _hoisted_4, toDisplayString(item.value.score), 1),
                        createElementVNode("span", _hoisted_5, "/" + toDisplayString(item.value.max), 1)
                      ]),
                      _: 1
                    }, 8, ["items"]),
                    props.config.showReviews ? (openBlock(), createBlock(unref(ArrowBtn), {
                      key: 0,
                      ref: "reviewsBtn",
                      onClick: _cache[3] || (_cache[3] = ($event) => {
                        currentView.value = unref(ViewType).REVIEWS;
                        unref(clearAutoClose)();
                      }),
                      class: "text-sm"
                    }, {
                      default: withCtx(() => [
                        createTextVNode(toDisplayString(unref(t)("Read Reviews")), 1)
                      ]),
                      _: 1
                    }, 512)) : createCommentVNode("", true)
                  ])) : createCommentVNode("", true)
                ], 512)) : createCommentVNode("", true)
              ])) : createCommentVNode("", true),
              createVNode(Transition, {
                name: "fade",
                appear: ""
              }, {
                default: withCtx(() => [
                  currentView.value === unref(ViewType).REVIEWS ? (openBlock(), createElementBlock("div", {
                    key: 0,
                    ref_key: "reviewsEl",
                    ref: reviewsEl,
                    class: "flex flex-col h-full",
                    "data-testid": "reviewsList"
                  }, [
                    createVNode(ReviewList, {
                      reviews: props.config.payload.reviews,
                      "review-languages": props.config.payload.reviewLanguages,
                      class: "flex-1 min-h-0"
                    }, null, 8, ["reviews", "review-languages"]),
                    createElementVNode("div", _hoisted_6, [
                      createVNode(unref(ActionButton), {
                        type: "back",
                        onActionClick: _cache[4] || (_cache[4] = ($event) => currentView.value = unref(ViewType).EXPANDED)
                      }),
                      createVNode(MainRating, {
                        data: props.config.payload.reviewAverages,
                        colors: {
                          darkText: unref(tailwindTheme).colors.grey[900],
                          lightText: unref(tailwindTheme).colors.grey[500]
                        },
                        orientation: "horizontal",
                        "show-info-btn": "",
                        tooltip: {
                          ...props.config.calculate.tooltip,
                          placement: "bottom"
                        },
                        class: "flex-1"
                      }, {
                        providersInfo: withCtx(() => [
                          createVNode(ProvidersInfo, {
                            data: props.config.calculate.providers,
                            direction: unref(textDirection)
                          }, null, 8, ["data", "direction"])
                        ]),
                        _: 1
                      }, 8, ["data", "colors", "tooltip"]),
                      createVNode(unref(ActionButton), {
                        type: "close",
                        onActionClick: _cache[5] || (_cache[5] = ($event) => currentView.value = unref(ViewType).COLLAPSED)
                      })
                    ])
                  ], 512)) : createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["aria-label", "position", "onMouseenter"]);
    };
  }
});

const FloatingDesktopReview = /* @__PURE__ */ _export_sfc$1(_sfc_main, [["__scopeId", "data-v-c0505781"]]);

export { FloatingDesktopReview as default };
//# sourceMappingURL=FloatingDesktopReview.D5AbSr-a.js.map
